import axios from 'axios';
import { useClubSlug } from 'components/Clubs/hooks';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useApi } from './useApi';
import useAuth from './useAuth';

export interface CheckPermissionOptions {
    clubSlug?: string;
    permission: string | string[];
    children?: React.ReactNode;
}

export const useCheckPermissions = ({
    clubSlug: _clubSlug,
    permission,
}: CheckPermissionOptions) => {
    const clubSlug = useClubSlug(_clubSlug);
    const { isAuthenticated } = useAuth();
    const [data, setData] = useState<any>({});
    const [isLoading, setIsLoading] = useState(clubSlug !== undefined);
    const permissions = useMemo(() => (Array.isArray(permission) ? permission : [permission]), [permission]);
    const cancellationToken = useRef(null);
    const api = useApi();
    const permissionError = useMemo(() => data?.error?.includes('PermissionDenied'), [data]);

    const checkPermission = () => {
        if (!isAuthenticated || !clubSlug || !!cancellationToken.current) {
            setIsLoading(false);
            return;
        }
        cancellationToken.current = axios.CancelToken.source();
        void api({
            method: 'GET',
            url: `clubs/${clubSlug}/check`,
            params: { permissions },
            cancelToken: cancellationToken.current.token,
            muted: true,
            onFailure: ({ data }) => setData(data),
            onSuccess: (data) => setData(data),
            onBefore: () => {
                setIsLoading(false);
                cancellationToken.current = undefined;
            }
        });
    };

    useEffect(() => {
        checkPermission();
        return () => {
            cancellationToken.current?.cancel();
        };
    }, [isAuthenticated, clubSlug]);

    return {
        data,
        isAuthenticated,
        permissionError,
        hasAccess: !permissionError,
        isLoading
    };
};

export default useCheckPermissions;
